/**
 * Derives decision-engine copy and scores from opportunity API payloads.
 * No invented company names; only uses fields present on the opportunity + signal list.
 */

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const round0 = (n) => Math.round(Number(n) || 0);

function parseDifficulty(opp) {
  const d = Number(opp?.difficulty);
  return Number.isFinite(d) ? clamp(d, 1, 10) : 5;
}

function competitionNorm(opp) {
  const c = String(opp?.competition || opp?.competition_level || "Medium").toLowerCase();
  if (c === "low") return "low";
  if (c === "high") return "high";
  return "medium";
}

/**
 * @param {Record<string, unknown>} opp
 * @param {{ velocity7d?: number, volatility14d?: number, sourceBreadth7dAvg?: number }} chartMeta
 */
export function deriveBuildVerdict(opp, chartMeta = {}) {
  const oppScore = clamp(Number(opp?.opportunity_score) || 0, 0, 100);
  const diff = parseDifficulty(opp);
  const saas = clamp(Number(opp?.saas_confidence) || 0.65, 0, 1);
  const comp = competitionNorm(opp);
  const confidencePct = clamp(Math.round(oppScore * 0.62 + saas * 34), 42, 96);

  const verdict = "Build Now";
  const line =
    "Signals are strong enough to move forward now. Keep scope focused, ship quickly, and iterate with real users.";
  const risk = "Low";

  let bestFit = "Solo founder";
  if (diff >= 7) bestFit = "Technical founder or small team";
  else if (String(opp?.niche || "").toLowerCase().includes("agency") || diff <= 4) {
    bestFit = comp === "high" ? "Agency / services-led" : "Solo founder";
  }
  if (saas < 0.45) bestFit = "Technical founder";
  if (comp === "high") bestFit = "Agency or team with distribution";

  return { verdict, line, confidencePct, risk, bestFit };
}

/**
 * @param {Record<string, unknown>} opp
 * @param {{ inferredRecency?: number }} ctx
 */
export function deriveActionableScores(opp, ctx = {}) {
  const oppScore = clamp(Number(opp?.opportunity_score) || 0, 0, 100);
  const trendScore = clamp(Number(opp?.trend_score) || oppScore, 0, 100);
  const momentum = clamp(Number(opp?.momentum) || 0, -1, 1);
  const mention = clamp(Number(opp?.mention_frequency) || 0, 0, 1);
  const recency = clamp(
    Number(opp?.recency) || ctx.inferredRecency || 0.5,
    0,
    1
  );
  const engagement = clamp(Number(opp?.engagement) || 0, 0, 1);
  const saas = clamp(Number(opp?.saas_confidence) || 0.65, 0, 1);
  const diff = parseDifficulty(opp);
  const comp = competitionNorm(opp);

  const mrrMin = Number(opp?.estimated_mrr_min) || 0;
  const mrrMax = Number(opp?.estimated_mrr_max) || 0;
  const mrrMid = (mrrMin + mrrMax) / 2;
  const rp = String(opp?.revenue_potential || "$$");
  const rpBonus = rp.includes("$$$") ? 16 : rp.includes("$$") ? 12 : 6;
  const monetizationPotential = clamp(
    round0(trendScore * 0.22 + saas * 28 + clamp(Math.log10(mrrMid + 1) * 18, 0, 36) + rpBonus),
    0,
    100
  );

  const marketDemand = clamp(
    round0(trendScore * 0.42 + mention * 100 * 0.28 + recency * 100 * 0.18 + engagement * 100 * 0.12),
    0,
    100
  );

  const competitionRisk = clamp(
    round0(comp === "high" ? 78 + diff * 2 : comp === "low" ? 22 + (10 - diff) * 2 : 48 + diff * 3),
    0,
    100
  );

  const ttm = String(opp?.time_to_mvp || "").toLowerCase();
  let speedBase = 72 - diff * 6;
  if (/week|jour|day|48h|72h/.test(ttm)) speedBase += 14;
  if (/month|quarter|year|trimestre|année|6\+|90 day/.test(ttm)) speedBase -= 22;
  const speedToMvp = clamp(round0(speedBase + momentum * 12), 0, 100);

  const founderFit = clamp(
    round0(saas * 42 + (10 - diff) * 5 + (comp === "low" ? 18 : comp === "medium" ? 8 : -4) + engagement * 22),
    0,
    100
  );

  return [
    {
      key: "demand",
      label: "Market Demand",
      value: marketDemand,
      line: "Blend of trend heat, mentions, and how fresh the conversation is across the radar.",
    },
    {
      key: "monetization",
      label: "Monetization Potential",
      value: monetizationPotential,
      line: "How well the idea maps to recurring revenue signals and the MRR band we estimate from the feed.",
    },
    {
      key: "competition",
      label: "Competition Risk",
      value: competitionRisk,
      line: "Higher means more crowded or harder to win attention—pair with a sharp wedge.",
    },
    {
      key: "speed",
      label: "Speed to MVP",
      value: speedToMvp,
      line: "Higher means a realistic faster first version given difficulty and stated time-to-MVP hints.",
    },
    {
      key: "fit",
      label: "Founder Fit",
      value: founderFit,
      line: "How approachable this looks for a small builder: SaaS clarity, competition, and execution load.",
    },
  ];
}

/**
 * @param {Record<string, unknown>} opp
 * @param {{ velocity7d?: number, trendDirection?: string }} meta
 */
export function deriveWhyRadar(opp, meta = {}) {
  const niche = String(opp?.niche || "this market").trim();
  const audience = String(opp?.target_audience || "").trim();
  const problem = String(opp?.problem_solved || "").trim();
  const why = String(opp?.why_trending || "").trim();
  const trend = String(opp?.trend_status || "rising");
  const momentum = Number(opp?.momentum) || 0;
  const v = Number(meta.velocity7d);
  const dir = meta.trendDirection || "Flat";

  const whyNow =
    why.length >= 40
      ? why.slice(0, 280) + (why.length > 280 ? "…" : "")
      : `We are surfacing this now because radar activity in ${niche} is marked ${trend} with momentum around ${(momentum * 100).toFixed(0)}% versus prior snapshots—enough to warrant a serious look, not just noise.`;

  const whatChanged =
    Number.isFinite(v) && Math.abs(v) >= 0.5
      ? `Opportunity score moved about ${v >= 0 ? "+" : ""}${v.toFixed(1)} points over the last week (${dir}), which usually reflects new mentions, engagement shifts, or broader pickup in the feed—not a guarantee of product-market fit.`
      : "Week-over-week score movement is still modest; treat the idea as structurally interesting rather than explosively accelerating until you see your own traction.";

  const whoLooking = audience
    ? audience
    : `Teams and operators in ${niche} discussing recurring pains similar to this pattern—use interviews to name the exact buyer and budget owner.`;

  const painPay = problem
    ? problem
    : "The strongest SaaS wins here reduce measurable time, risk, or revenue leakage—validate which metric moves for your buyer before you scope features.";

  return { whyNow, whatChanged, whoLooking, painPay };
}

/**
 * @param {Record<string, unknown>} opp
 * @param {Record<string, unknown>[]} signals
 */
export function deriveLaunchStrategy(opp, signals) {
  const niche = String(opp?.niche || "your niche").trim();
  const problem = String(opp?.problem_solved || opp?.short_description || "").trim();
  const audience = String(opp?.target_audience || `buyers in ${niche}`).trim();
  const bm = String(opp?.business_model || "Subscription SaaS").trim();
  const sources = Array.isArray(opp?.sources) ? opp.sources.map(String) : [];

  const mvp =
    problem.length > 24
      ? `A single-workflow product that solves the core part of: ${problem.slice(0, 160)}${problem.length > 160 ? "…" : ""}`
      : `A narrow workflow tool for ${audience.split(",")[0] || niche} with one killer outcome and manual onboarding.`;

  const firstNiche = `${niche} · ${audience.split(".")[0] || audience}`.slice(0, 200);

  const rp = String(opp?.revenue_potential || "$$");
  let pricing = "Start with a simple tiered subscription (starter / pro) and annual discount once you have 10 paying conversations.";
  if (rp.includes("$$$"))
    pricing = "Premium ACV positioning: annual-first pricing, clear ROI story, and onboarding that justifies a higher ticket.";
  else if (rp.includes("$") && !rp.includes("$$$"))
    pricing = "Land with an accessible monthly entry tier, then expand via usage seats or add-ons as value proves out.";

  let channel =
    "Founder-led outbound to identifiable accounts plus one community where your buyers already gather.";
  if (sources.some((s) => /reddit|hackernews|lobsters/i.test(s)))
    channel = "Community-led: ship in public, share build logs, and convert engaged threads into waitlist demos.";
  else if (sources.some((s) => /producthunt|ph/i.test(s)))
    channel = "Launch narrative + early-access list; pair a Product Hunt moment with direct outreach to niche power users.";
  else if (sources.some((s) => /indie|revenue|stripe/i.test(s)))
    channel = "Indie/SMB motion: transparent metrics, founder Twitter/LinkedIn, and peer referrals inside small-team tools.";

  const nameShort = String(opp?.name || "This idea").slice(0, 48);
  const plan = [
    { day: 1, action: `Write a one-page thesis: who pays, for what outcome, for ${nameShort}.` },
    { day: 2, action: "Book 5 discovery calls; ask for current workflow, tools, and budget signals only." },
    { day: 3, action: "Publish a landing with waitlist + manual concierge offer tied to the core workflow." },
    { day: 4, action: "Scope the smallest shippable workflow (one path, no settings maze) from call notes." },
    { day: 5, action: "Build the MVP slice; instrument one success event that proves value delivery." },
    { day: 6, action: "Onboard 3 testers manually; fix blockers live; capture quotes and objections." },
    { day: 7, action: "Decide go/no-go: pricing test, calendar link for paid pilot, or pivot the wedge." },
  ];

  return {
    suggestedMvp: mvp,
    firstNiche,
    pricingSuggestion: pricing,
    acquisitionChannel: channel,
    businessModelNote: bm,
    plan7: plan,
  };
}

const COMMUNITY = new Set(["reddit", "hackernews", "lobsters", "indiehackers", "producthunt", "devto"]);
const REVENUE = new Set(["trustmrr"]);

/**
 * @param {Record<string, unknown>} opp
 * @param {Record<string, unknown>[]} signals
 */
export function deriveSignalBreakdown(opp, signals) {
  const total = Math.max(0, round0(opp?.signal_count));
  let community = 0;
  let searchIntent = 0;
  let competitor = 0;
  let revenue = 0;

  const list = Array.isArray(signals) ? signals : [];
  if (list.length) {
    let weak = 0;
    for (const s of list) {
      const src = String(s?.source || "").toLowerCase();
      const eng = s?.engagement && typeof s.engagement === "object" ? s.engagement : {};
      const si = Number(eng.search_impressions) || 0;
      if (!src && si <= 0) {
        weak += 1;
        continue;
      }
      if (REVENUE.has(src)) revenue += 1;
      else if (COMMUNITY.has(src)) community += 1;
      else if (si > 0) searchIntent += 1;
      else if (src) competitor += 1;
      else weak += 1;
    }
    return {
      mode: "per_signal",
      rows: [
        { key: "community", label: "Community signals", count: community, note: "Discussions, launches, and threads in public communities." },
        { key: "search", label: "Search intent signals", count: searchIntent, note: "Signals where search-style demand metrics appeared on the item." },
        { key: "competitor", label: "Competitor / market signals", count: competitor, note: "Listings, catalogs, or other non-community sources in the feed." },
        { key: "revenue", label: "Revenue signals", count: revenue, note: "Items with explicit revenue or MRR-shaped engagement fields." },
        { key: "weak", label: "Weak signals / uncertainty", count: weak, note: "Sparse metadata or unknown source—treat as lower confidence when sizing demand." },
      ],
      total: list.length,
    };
  }

  if (total <= 0) {
    return {
      mode: "estimated",
      rows: [
        { key: "community", label: "Community signals", count: 0, note: "No aggregate signal count yet; check back after the next radar run." },
        { key: "search", label: "Search intent signals", count: 0, note: "—" },
        { key: "competitor", label: "Competitor signals", count: 0, note: "—" },
        { key: "revenue", label: "Revenue signals", count: 0, note: "—" },
        { key: "weak", label: "Weak signals / uncertainty", count: 0, note: "Not enough feed volume to split; treat scores as directional only." },
      ],
      total: 0,
    };
  }

  const div = clamp(Number(opp?.source_diversity) || 0.35, 0.08, 1);
  const mention = clamp(Number(opp?.mention_frequency) || 0.25, 0.08, 1);
  const eng = clamp(Number(opp?.engagement) || 0.2, 0.05, 1);
  const wC = 0.28 + div * 0.22;
  const wS = 0.18 + mention * 0.2;
  const wR = 0.12 + eng * 0.18;
  const wComp = 0.22 + (1 - div) * 0.15;
  const sum = wC + wS + wR + wComp;
  community = Math.max(1, round0((total * wC) / sum));
  searchIntent = Math.max(0, round0((total * wS) / sum));
  revenue = Math.max(0, round0((total * wR) / sum));
  competitor = Math.max(0, total - community - searchIntent - revenue);
  const estTotal = community + searchIntent + competitor + revenue;
  const weak = Math.max(0, total - estTotal);

  return {
    mode: "estimated",
    rows: [
      { key: "community", label: "Community signals", count: community, note: "Estimated share from diversity + volume (no per-source names invented)." },
      { key: "search", label: "Search intent signals", count: searchIntent, note: "Estimated from mention density vs. breadth in the radar model." },
      { key: "competitor", label: "Competitor signals", count: competitor, note: "Residual market noise implied when many sources overlap the same idea." },
      { key: "revenue", label: "Revenue signals", count: revenue, note: "Estimated from engagement-weighted business signal contribution." },
      { key: "weak", label: "Weak signals / uncertainty", count: weak, note: "Rounding slack—treat as unknown mix until more items are ingested." },
    ],
    total,
  };
}

/**
 * @param {Record<string, unknown>} opp
 * @param {{ verdict?: string }} verdictCtx
 */
export function deriveRisks(opp, verdictCtx = {}) {
  const comp = competitionNorm(opp);
  const diff = parseDifficulty(opp);
  const trend = String(opp?.trend_status || "").toLowerCase();
  const problem = String(opp?.problem_solved || "").trim();

  const mainRisk =
    comp === "high"
      ? "Winner-takes-most dynamics: distribution and differentiation cost can dominate product work."
      : diff >= 8
        ? "Shipping risk: scope and integrations can push timelines far beyond a solo calendar."
        : trend === "saturated" || trend === "cooling"
          ? "Timing risk: narrative may be past peak unless you find an underserved wedge."
          : "Execution risk: you still need crisp ICP, pricing, and onboarding—scores do not remove go-to-market work.";

  const saturation =
    ["saturated", "peak", "cooling"].includes(trend)
      ? "Elevated: multiple players and loud marketing noise in similar positioning."
      : "Moderate: monitor new entrants weekly if you see fast follower launches in the niche.";

  const execution = diff <= 4 ? "Moderate — keep scope aggressively small." : diff <= 6 ? "Meaningful — plan for integrations and edge cases." : "High — budget for senior engineering or a partner.";

  const couldFail =
    !problem
      ? "Fails if the pain is episodic or buyers refuse to adopt another tool in an already crowded stack."
      : "Fails if buyers downgrade the pain after a pilot, or switching costs stay higher than your incremental value.";

  const validateBefore = [
    "3–5 paid discovery calls that end with a concrete next step (trial, pilot, or clear no).",
    "A one-metric success definition for the MVP (time saved, tickets avoided, revenue recovered, etc.).",
    "Pricing hypothesis tested on paper before you automate billing.",
  ];

  if (verdictCtx.verdict === "Avoid") {
    validateBefore.unshift("Re-check competition mapping: who already wins SEO, partnerships, and integrations?");
  }

  return { mainRisk, saturation, execution, couldFail, validateBefore };
}
